/**
 * Pure state transitions for the design SEO regeneration wizard.
 *
 * The preview returned by seo-service stays immutable. User choices and edits
 * live alongside it, which makes resetting generated values and building the
 * optimistic-locking request deterministic and easy to test without React.
 */

import type {
  SeoDesignChannelMarketplace,
  SeoDesignField,
  SeoDesignRegenerationApplyRequest,
  SeoDesignRegenerationPreviewRow,
} from '../../seo/api'

const DESIGN_FIELDS: readonly SeoDesignField[] = ['title', 'description']
const THEME_DISPLAY_STOP_LIST = new Set(['мужчине', 'девушке'])

export interface RegenerateSeoWizardRow {
  preview: SeoDesignRegenerationPreviewRow
  included: boolean
  fields: Record<SeoDesignField, string>
}

export interface RegenerateSeoWizardProductGroup {
  article: string
  productTitle: string | null
  rows: RegenerateSeoWizardRow[]
}

export interface RegenerateSeoWizardSummary {
  total: number
  changed: number
  unchanged: number
  blocked: number
}

const fieldValue = (
  fields: Partial<Record<SeoDesignField, string>>,
  field: SeoDesignField,
): string => fields[field] ?? ''

const proposedFields = (row: SeoDesignRegenerationPreviewRow): Record<SeoDesignField, string> => ({
  title: fieldValue(row.proposed, 'title'),
  description: fieldValue(row.proposed, 'description'),
})

const uniqueFields = (fields: readonly SeoDesignField[]): SeoDesignField[] =>
  DESIGN_FIELDS.filter((field) => fields.includes(field))

const writableFields = (
  row: RegenerateSeoWizardRow,
  selectedFields: readonly SeoDesignField[],
): SeoDesignField[] => {
  const locked = new Set(row.preview.skipped_locked)
  return uniqueFields(selectedFields).filter((field) => !locked.has(field))
}

/** Every preview row starts selected and carries an editable copy of proposed text. */
export function initRegenerateSeoRows(
  rows: readonly SeoDesignRegenerationPreviewRow[],
): RegenerateSeoWizardRow[] {
  return rows.map((preview) => ({
    preview,
    included: true,
    fields: proposedFields(preview),
  }))
}

/** Groups channels under products while retaining the backend row order. */
export function groupRegenerateSeoRowsByArticle(
  rows: readonly RegenerateSeoWizardRow[],
): RegenerateSeoWizardProductGroup[] {
  const groups = new Map<string, RegenerateSeoWizardProductGroup>()
  for (const row of rows) {
    const { article, product_title: productTitle } = row.preview
    const existing = groups.get(article)
    if (existing) {
      existing.rows.push(row)
      if (!existing.productTitle && productTitle) existing.productTitle = productTitle
      continue
    }
    groups.set(article, { article, productTitle, rows: [row] })
  }
  return [...groups.values()]
}

export function editRegenerateSeoRowField(
  rows: readonly RegenerateSeoWizardRow[],
  contentId: number,
  field: SeoDesignField,
  value: string,
): RegenerateSeoWizardRow[] {
  return rows.map((row) =>
    row.preview.content_id === contentId
      ? { ...row, fields: { ...row.fields, [field]: value } }
      : row,
  )
}

export function resetRegenerateSeoRow(
  rows: readonly RegenerateSeoWizardRow[],
  contentId: number,
): RegenerateSeoWizardRow[] {
  return rows.map((row) =>
    row.preview.content_id === contentId
      ? { ...row, fields: proposedFields(row.preview) }
      : row,
  )
}

export function resetAllRegenerateSeoRows(
  rows: readonly RegenerateSeoWizardRow[],
): RegenerateSeoWizardRow[] {
  return rows.map((row) => ({ ...row, fields: proposedFields(row.preview) }))
}

export function setRegenerateSeoRowIncluded(
  rows: readonly RegenerateSeoWizardRow[],
  contentId: number,
  included: boolean,
): RegenerateSeoWizardRow[] {
  return rows.map((row) =>
    row.preview.content_id === contentId ? { ...row, included } : row,
  )
}

export function regenerateSeoRowDirtyFields(
  row: RegenerateSeoWizardRow,
): SeoDesignField[] {
  return DESIGN_FIELDS.filter(
    (field) => row.fields[field] !== fieldValue(row.preview.proposed, field),
  )
}

/** Dirty means manually different from generated proposal, not from current content. */
export function isRegenerateSeoRowDirty(row: RegenerateSeoWizardRow): boolean {
  return regenerateSeoRowDirtyFields(row).length > 0
}

function rowWillChange(
  row: RegenerateSeoWizardRow,
  selectedFields: readonly SeoDesignField[],
): boolean {
  return writableFields(row, selectedFields).some(
    (field) => row.fields[field] !== fieldValue(row.preview.current, field),
  )
}

/** Summary is always scoped to included rows and fields that can actually be written. */
export function summarizeRegenerateSeoRows(
  rows: readonly RegenerateSeoWizardRow[],
  selectedFields: readonly SeoDesignField[] = DESIGN_FIELDS,
): RegenerateSeoWizardSummary {
  const included = rows.filter((row) => row.included)
  const changed = included.filter((row) => rowWillChange(row, selectedFields)).length
  return {
    total: included.length,
    changed,
    unchanged: included.length - changed,
    blocked: included.filter((row) => row.preview.blocked).length,
  }
}

export interface BuildRegenerateSeoApplyRequestOptions {
  designCode: string | number
  rows: readonly RegenerateSeoWizardRow[]
  fields: readonly SeoDesignField[]
  comment: string
  approve?: SeoDesignRegenerationApplyRequest['approve']
}

/**
 * Builds only the rows and fields the operator selected. Locked fields never
 * leave the browser; rows without a writable selected field are omitted.
 */
export function buildRegenerateSeoApplyRequest({
  designCode,
  rows,
  fields,
  comment,
  approve = false,
}: BuildRegenerateSeoApplyRequestOptions): SeoDesignRegenerationApplyRequest {
  const requestRows: SeoDesignRegenerationApplyRequest['rows'] = []
  for (const row of rows) {
    if (!row.included) continue
    const nextFields: Partial<Record<SeoDesignField, string>> = {}
    for (const field of writableFields(row, fields)) nextFields[field] = row.fields[field]
    if (Object.keys(nextFields).length === 0) continue
    requestRows.push({
      content_id: row.preview.content_id,
      expected_revision: row.preview.expected_revision,
      fields: nextFields,
    })
  }
  return {
    design_code: String(designCode),
    rows: requestRows,
    comment,
    approve,
  }
}

const normalizeTheme = (value: string): string =>
  value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru')

export function isThemeDisplayStopListed(value: string | null | undefined): boolean {
  return THEME_DISPLAY_STOP_LIST.has(normalizeTheme(String(value ?? '')))
}

export function buildRegenerateSeoComment(
  previousTheme: string | null | undefined,
  nextTheme: string | null | undefined,
): string {
  const previous = String(previousTheme ?? '').trim()
  const next = String(nextTheme ?? '').trim() || previous
  return `Перегенерация SEO из карточки макета: «${previous}» → «${next}»`
}

/** Normalizes mp_sellers values to the short codes accepted by SEO endpoints. */
export function normalizeDesignChannelMarketplace(
  value: string | null | undefined,
): SeoDesignChannelMarketplace | null {
  const normalized = String(value ?? '').trim().toLocaleLowerCase('ru')
  if (normalized === 'wb' || normalized.startsWith('wild')) return 'wb'
  if (normalized === 'ozon' || normalized === 'oz' || normalized.startsWith('ozon')) return 'ozon'
  if (normalized === 'ym' || normalized === 'yandex_market' || normalized === 'yandex market') return 'ym'
  if (normalized === 'site') return 'site'
  return null
}

export interface RegenerateSeoLinkTarget {
  marketplace: SeoDesignChannelMarketplace
  seller_id: number
  article: string
}

export function buildRegenerateSeoResultLink(
  target: RegenerateSeoLinkTarget | null | undefined,
): string | null {
  if (!target) return null
  const params = new URLSearchParams({
    mp: target.marketplace,
    seller: String(target.seller_id),
    article: target.article,
  })
  return `/products-seo-v2?${params.toString()}`
}
